/**
 * Página independiente de palabras guardadas. Misma estructura que la pestaña
 * "guardadas" de Progreso (secciones "Esta semana" / "Todo el historial" con
 * filas {@link VocabCard}), pero como página propia con cabecera del dashboard.
 */

import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Spinner } from '@/components/atoms/Spinner';
import { VocabCard } from '@/components/molecules/VocabCard';
import { buildRoute, ROUTES } from '@/constants/routes';
import { getWeekStartMs } from '@/pages/student/ProgressPage/utils/wordPeriodRanges';
import { savedVocabularyToVocabItem } from '@/pages/student/LearnedWordsPages/utils/savedVocabularyToVocabItem';
import { useSavedVocabulary } from '@/context/SavedVocabularyContext';
import type { VocabItem } from '@/types/vocabulary.types';
import styles from './SavedWordsPages.module.scss';

export function SavedWordsListPage() {
  const { t } = useTranslation();
  const { savedItems, removeItem, isLoading } = useSavedVocabulary();

  const savedVocabRows = useMemo(
    () => [...savedItems].sort((a, b) => b.savedAt - a.savedAt),
    [savedItems]
  );

  // Misma división que la vista de Progreso: las dos secciones no se solapan.
  const savedVocabSplit = useMemo(() => {
    const weekStart = getWeekStartMs();
    return {
      thisWeek: savedVocabRows.filter((s) => s.savedAt >= weekStart),
      older: savedVocabRows.filter((s) => s.savedAt < weekStart),
    };
  }, [savedVocabRows]);

  const handleRemoveSaved = useCallback(
    async (item: VocabItem) => {
      await removeItem(item.word);
    },
    [removeItem]
  );

  const renderRow = (rowId: string, item: VocabItem) => (
    <VocabCard
      key={rowId}
      item={item}
      detailHref={buildRoute(ROUTES.STUDENT.SAVED_WORD_DETAIL, { savedId: rowId })}
      isSaved
      showSaveButton
      saveActionVariant="trash"
      showTranslation={false}
      onToggleSave={handleRemoveSaved}
    />
  );

  return (
    <div className={styles.page}>
      <header className={styles.page__header}>
        <h1 className={styles.page__title}>{t('savedWordsPage.title')}</h1>
        <p className={styles.page__subtitle}>{t('savedWordsPage.subtitle')}</p>
      </header>

      {isLoading ? (
        <div className={styles.page__state}>
          <Spinner size="lg" aria-label={t('common.loading')} />
        </div>
      ) : savedVocabRows.length === 0 ? (
        <div className={styles.page__state}>
          <p>{t('learnedWordsList.savedEmpty')}</p>
        </div>
      ) : (
        <div className={styles.page__sections}>
          <section className={styles.page__section} aria-labelledby="saved-section-week">
            <h2 id="saved-section-week" className={styles.page__sectionTitle}>
              {t('learnedWordsList.sectionThisWeek')}
            </h2>
            {savedVocabSplit.thisWeek.length === 0 ? (
              <p className={styles.page__sectionEmpty}>{t('learnedWordsList.savedEmptyThisWeek')}</p>
            ) : (
              <div className={styles.page__list}>
                {savedVocabSplit.thisWeek.map((row) =>
                  renderRow(row.id, savedVocabularyToVocabItem(row))
                )}
              </div>
            )}
          </section>

          <section className={styles.page__section} aria-labelledby="saved-section-all">
            <h2 id="saved-section-all" className={styles.page__sectionTitle}>
              {t('learnedWordsList.sectionAllTime')}
            </h2>
            {savedVocabSplit.older.length === 0 ? (
              <p className={styles.page__sectionEmpty}>
                {t('learnedWordsList.savedEmptyAllOlderInWeek')}
              </p>
            ) : (
              <div className={styles.page__list}>
                {savedVocabSplit.older.map((row) =>
                  renderRow(row.id, savedVocabularyToVocabItem(row))
                )}
              </div>
            )}
          </section>
        </div>
      )}
    </div>
  );
}

SavedWordsListPage.displayName = 'SavedWordsListPage';
export default SavedWordsListPage;
